import React, { useMemo, useState } from "react";
import type { AppProps } from "next/app";
import { UserProvider } from "@auth0/nextjs-auth0/client";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  BarElement,
  Title,
  ArcElement,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
} from "chart.js";
import { Analytics } from "@vercel/analytics/react";

import { createTheme, ThemeProvider } from "@mui/material";

import "../styles/globals.css";

import { ReportContext } from "contexts/reportContext";
import { type ReportItemType } from "interfaces";
import Head from "next/head";
import { themeClass, wrapper } from "styles/theme.css";
import { type KnownFields } from "shared/known-fields.enum";
import { lemonSqueezySetup } from "@lemonsqueezy/lemonsqueezy.js";
import { clsx } from "uikit/utils";
import Layout from "components/Layout";
ChartJS.register(
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  PointElement,
  LineController,
  LineElement
);

lemonSqueezySetup({
  apiKey: process.env.LEMON_SQUEEZY_KEY,
  onError(error) {
    console.log(error);
  },
});

const theme = createTheme({
  palette: {
    mode: "dark",
  },
});

export default function App({ Component, pageProps }: AppProps) {
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [dataParsed, setDataParsed] = useState<ReportItemType[]>([]);
  const [mappingValues, setMappingValues] = useState<KnownFields[]>([]);
  const [uploads, setUploads] = useState<number>(0);

  // useEffect(() => {
  //   // if ("serviceWorker" in navigator) {
  //   //   navigator.serviceWorker
  //   //     .register("/service-worker.js")
  //   //     .then((registration) => {
  //   //       console.log("scope is: ", registration.scope);
  //   //     })
  //   //     .catch((err) => {
  //   //       console.error("service worker registration failed", err.message);
  //   //     });
  //   // }
  // }, [user]);

  const contextData = useMemo(
    () => ({
      report: dataParsed,
      setReport: (arg: ReportItemType[]) => {
        setDataParsed(arg);
      },
      rawReportData: csvData,
      setRawReportData: (arg: string[][]) => {
        setCsvData(arg);
        setMappingValues(
          arg[0]?.length ? new Array(arg[0].length).fill("") : []
        );
      },
      mappings: mappingValues,
      setMappings: (arg: KnownFields[]) => {
        setMappingValues(arg);
      },
      uploads,
      setUploads,
    }),
    [
      setDataParsed,
      setCsvData,
      setMappingValues,
      dataParsed,
      csvData,
      mappingValues,
      uploads,
      setUploads,
    ]
  );

  return (
    <>
      <Head>
        <title>StatAnalyzer</title>
        <link rel="icon" href="/icon-light.svg" />
        <meta name="description" content="Bank statement analysis" />
        {/* <link rel="manifest" href="/manifest.json"></link> */}
      </Head>
      <ReportContext.Provider value={contextData}>
        <ThemeProvider theme={theme}>
          <UserProvider>
            <Layout>
              <div className={clsx(wrapper, themeClass)}>
                <Component {...pageProps} />
                <Analytics />
              </div>
            </Layout>
          </UserProvider>
        </ThemeProvider>
      </ReportContext.Provider>
    </>
  );
}
